import { useCallback, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { List, postUpdateList, UpdateListPayload } from "../../api/lists";
import { READING_LISTS_SHELF_QUERY_KEY } from "../../screens/shelf/hooks/useReadingListsShelf";
import { readingListDetailQueryKey } from "../../screens/shelf/ReadingListDetailScreen";

/**
 * Local edit state for the title + description "drafted" save model. The
 * draft is seeded from the server list on mount and reset whenever the
 * underlying list id changes (so navigating to another list doesn't bleed
 * one curator's draft into another).
 *
 * Visibility lives outside this hook because it's instant-save — there's no
 * draft state to manage.
 */
export function useReadingListEditDraft(list: List) {
  const [draftName, setDraftName] = useState(list.display_name);
  const [draftDescription, setDraftDescription] = useState(
    list.description ?? "",
  );

  // Reset whenever we navigate to a different list. We key off id rather than
  // the entire list object so that an in-place server refresh (e.g. after a
  // visibility toggle) doesn't clobber the user's in-flight title edit.
  useEffect(() => {
    setDraftName(list.display_name);
    setDraftDescription(list.description ?? "");
  }, [list.id]);

  // Compare against the server-side values, normalising empty descriptions to
  // null so that "" and undefined both compare clean.
  const trimmedName = draftName.trim();
  const trimmedDescription = draftDescription.trim();
  const dirtyName = trimmedName !== list.display_name.trim();
  const dirtyDescription =
    (trimmedDescription || null) !== ((list.description ?? "").trim() || null);
  const isDirty = dirtyName || dirtyDescription;
  // Title is required — empty drafts are dirty but un-saveable.
  const canSave = isDirty && trimmedName.length > 0;

  const reset = useCallback(() => {
    setDraftName(list.display_name);
    setDraftDescription(list.description ?? "");
  }, [list.display_name, list.description]);

  return {
    draftName,
    setDraftName,
    draftDescription,
    setDraftDescription,
    isDirty,
    canSave,
    reset,
  };
}

/**
 * Mutations the editor needs. Each one invalidates the lists shelf query on
 * success so the rail row + detail pane both pick up the new state.
 *
 * Visibility uses an *optimistic* update — flipping a switch should feel
 * instant; we patch the cache, fire the request, and roll back on failure.
 *
 * Title/description don't optimistically update because they're saved through
 * the sticky bar (which already gives the user a clear confirmation moment
 * via the toast).
 */
export function useReadingListEditMutations(list: List) {
  const [t] = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const invalidateShelf = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: READING_LISTS_SHELF_QUERY_KEY });
  }, [queryClient]);

  // Detail-pane query — owned by ReadingListDetailScreen. Invalidating both
  // the shelf and the detail keeps the rail and the open pane in sync after
  // an edit. Used to live inline on the pane (the old isEditing flow
  // refetched implicitly on Done); now that editing lives in a separate
  // modal, the cache the pane reads from has to be told to refetch
  // explicitly.
  const invalidateDetail = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: readingListDetailQueryKey(list.id) });
  }, [queryClient, list.id]);

  // ------------- Title + description (explicit save) ----------------------
  const saveMetadata = useMutation({
    mutationFn: (payload: UpdateListPayload) => postUpdateList(list.id, payload),
    onSuccess: () => {
      invalidateShelf();
      invalidateDetail();
      toast({
        title: t("screens.shelf.readingLists.detail.editor.saveSuccess"),
        status: "success",
        duration: 2500,
        isClosable: true,
      });
    },
    onError: () => {
      toast({
        title: t("screens.shelf.readingLists.detail.editor.saveError"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    },
  });

  // ------------- Visibility (instant save with optimistic flip) -----------
  const toggleVisibility = useMutation({
    mutationFn: (nextIsPublic: boolean) =>
      postUpdateList(list.id, { is_public: nextIsPublic }),
    onMutate: async (nextIsPublic) => {
      await queryClient.cancelQueries({ queryKey: READING_LISTS_SHELF_QUERY_KEY });
      // Snapshot the entire shelf cache so we can roll back any list shape
      // the API returns (we don't depend on a single normalised shape here).
      const previous = queryClient.getQueriesData({
        queryKey: READING_LISTS_SHELF_QUERY_KEY,
      });
      // Patch every cached list with the matching id.
      queryClient.setQueriesData(
        { queryKey: READING_LISTS_SHELF_QUERY_KEY },
        (data: unknown) => patchListInResponse(data, list.id, { is_public: nextIsPublic }),
      );
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      // Roll back to the snapshot we took in onMutate.
      if (ctx?.previous) {
        for (const [key, value] of ctx.previous) {
          queryClient.setQueryData(key, value);
        }
      }
      toast({
        title: t("screens.shelf.readingLists.detail.editor.visibilityError"),
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    },
    onSuccess: (_, nextIsPublic) => {
      toast({
        title: nextIsPublic
          ? t("screens.shelf.readingLists.detail.editor.nowPublic")
          : t("screens.shelf.readingLists.detail.editor.nowPrivate"),
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    },
    onSettled: () => {
      // Re-sync from the server in case the cache patch missed a denormalised
      // copy of the list somewhere. Invalidate the detail too so the modal-
      // driven flip propagates to the pane the user came from.
      invalidateShelf();
      invalidateDetail();
    },
  });

  return {
    saveMetadata,
    toggleVisibility,
  };
}

// Patches the matching list inside the shelf cache shape (an axios envelope:
// `.data.payload.results: List[]`). Anything that doesn't match the expected
// shape passes through untouched so we never blow up on an unrelated cache.
function patchListInResponse(
  data: unknown,
  listId: number,
  patch: Partial<List>,
): unknown {
  if (!data || typeof data !== "object") return data;
  const envelope = data as {
    data?: { payload?: { results?: List[] } };
  };
  const results = envelope.data?.payload?.results;
  if (!Array.isArray(results)) return data;
  return {
    ...envelope,
    data: {
      ...envelope.data,
      payload: {
        ...envelope.data!.payload,
        results: results.map((entry) =>
          entry?.id === listId ? { ...entry, ...patch } : entry,
        ),
      },
    },
  };
}
